import React, { useEffect, useMemo, useState } from 'react';

import { Box, Chip, CircularProgress, Typography } from '@mui/material';
import dayjs from 'dayjs';

import { ButtonUI, DataGridIdentifiers, DatagridPremiumUi, DialogUI } from '../Interface';
import { dataGridPremiumColumns } from '../../utils/helper';
import { formatCurrency } from '../../utils/currencyHelper';
import documentService from '../../service/newDoc.service';

const getFirstDefined = (...values) => values.find((value) => value !== undefined && value !== null && value !== '');

const toNumericValue = (value) => {
  if (value === null || value === undefined || value === '') return null;

  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const getResponseRows = (response) => {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.rows)) return response.rows;
  if (Array.isArray(response?.data)) return response.data;
  if (Array.isArray(response?.items)) return response.items;

  return [];
};

const getPartnerDisplayName = (row) => {
  return getFirstDefined(
    row.companyName,
    row.companyShortName,
    row.partnerName,
    row.bpName,
    row.customerName,
    row.supplierName,
    row.doc?.companyName,
    row.doc?.companyShortName,
    row.company?.companyName,
    row.company?.companyShortName,
    row.company?.name,
    row.company?.shortName
  );
};

const getPartnerId = (row) => {
  return getFirstDefined(
    row.companyId,
    row.partnerId,
    row.bpId,
    row.doc?.companyId,
    row.company?.companyId,
    row.company?.id
  );
};

const getDocumentDate = (row) => {
  return getFirstDefined(
    row.docDate,
    row.documentDate,
    row.date,
    row.doc?.docDate,
    row.doc?.date,
    row.issueDate,
    row.doc?.issueDate
  );
};

const normalizeRows = (rows, docTypes) => {
  return rows.map((row, index) => {
    const resolvedDocTypeId = getFirstDefined(row.docTypeId, row.documentTypeId, row.doc?.docTypeId);
    const docType = docTypes.find((item) => Number(item.documentTypeId) === Number(resolvedDocTypeId));
    const docDate = getDocumentDate(row);
    const docNum = getFirstDefined(
      row.docNum,
      row.documentNumber,
      row.number,
      row.doc?.docNum,
      row.doc?.documentNumber
    );

    return {
      id: getFirstDefined(
        row.id,
        row.lineId,
        row.docLineId,
        `${resolvedDocTypeId ?? 'doc'}-${docNum ?? 'line'}-${index}`
      ),
      docTypeId: resolvedDocTypeId,
      docTypeName: getFirstDefined(docType?.name, row.docTypeName, row.documentTypeName, row.doc?.docTypeName),
      direction: getFirstDefined(docType?.direction, row.direction, row.doc?.direction),
      isInvoice: Boolean(getFirstDefined(docType?.invoice, row.invoice, row.isInvoice, row.doc?.invoice)),
      docDate,
      partnerId: getPartnerId(row),
      partnerName: getPartnerDisplayName(row),
      docNum,
      qty: toNumericValue(getFirstDefined(row.qty, row.quantity, row.docQty, row.lineQty)),
      priceUnitPrice: toNumericValue(
        getFirstDefined(
          row.priceUnitPrice,
          row.baseUnitPrice,
          row.basePrice,
          row.unitPriceBeforeDiscount,
          row.originalUnitPrice
        )
      ),
      priceCurrencyCode: getFirstDefined(
        row.priceCurrencyCode,
        row.currencyCode,
        row.currency,
        row.doc?.currencyCode
      ),
      discountPercent: toNumericValue(getFirstDefined(row.discountPercent, row.discount, row.discountPct)),
      unitPrice: toNumericValue(getFirstDefined(row.unitPrice, row.price, row.netUnitPrice)),
      currencyCode: getFirstDefined(row.currencyCode, row.currency, row.doc?.currencyCode),
    };
  });
};

const formatNumber = (value) => {
  if (value === null || value === undefined) return '';
  return formatCurrency(value);
};

const DocumentPrevPricesDialog = ({
  open,
  onClose,
  itemCode,
  itemName,
  documentTypeId,
  currentDirection,
  docTypes,
  initialPartnerId,
  initialPartnerName,
}) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !itemCode || !documentTypeId) return;

    let active = true;
    setLoading(true);

    documentService
      .getPrevPrices({
        docTypeId: documentTypeId,
        itemCode,
      })
      .then((response) => {
        if (!active) return;

        const normalizedRows = normalizeRows(getResponseRows(response), docTypes);
        setRows(normalizedRows);
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [open, itemCode, documentTypeId, docTypes]);

  const filteredRows = useMemo(() => {
    return [...rows]
      .filter((row) => {
        if (
          currentDirection === undefined ||
          currentDirection === null ||
          row.direction === undefined ||
          row.direction === null
        ) {
          return true;
        }

        return String(row.direction) === String(currentDirection);
      })
      .sort((left, right) => {
        const leftDate = left.docDate ? dayjs(left.docDate).valueOf() : 0;
        const rightDate = right.docDate ? dayjs(right.docDate).valueOf() : 0;
        return rightDate - leftDate;
      });
  }, [rows, currentDirection]);

  const gridInitialState = useMemo(() => {
    const filterItems = initialPartnerName
      ? [
          {
            id: 1,
            field: 'partnerName',
            operator: 'contains',
            value: initialPartnerName,
          },
        ]
      : [];

    return {
      density: 'compact',
      filter: {
        filterModel: {
          items: filterItems,
          logicOperator: 'and',
        },
      },
      sorting: {
        sortModel: [{ field: 'docDate', sort: 'desc' }],
      },
    };
  }, [initialPartnerName]);

  const columns = useMemo(
    () =>
      dataGridPremiumColumns([
        {
          field: 'docTypeName',
          headerName: 'Bizonylat típus',
          type: 'string',
          minWidth: 180,
          renderCell: (params) => {
            if (!params.value) return '';

            return (
              <Chip
                label={params.value}
                size="small"
                variant={params.row.isInvoice ? 'filled' : 'outlined'}
                color={params.row.isInvoice ? 'warning' : 'default'}
              />
            );
          },
        },
        {
          field: 'docDate',
          headerName: 'Dátum',
          type: 'date',
          minWidth: 120,
          valueGetter: (_value, row) => (row.docDate ? new Date(row.docDate) : null),
          renderCell: (params) => (params.row.docDate ? dayjs(params.row.docDate).format('YYYY.MM.DD') : ''),
        },
        {
          field: 'partnerName',
          headerName: 'Partner név',
          type: 'string',
          minWidth: 200,
        },
        {
          field: 'docNum',
          headerName: 'Bizonylatszám',
          type: 'string',
          minWidth: 160,
        },
        {
          field: 'qty',
          headerName: 'Mennyiség',
          type: 'number',
          minWidth: 110,
          align: 'right',
          headerAlign: 'right',
          renderCell: (params) => formatNumber(params.row.qty),
        },
        {
          field: 'priceUnitPrice',
          headerName: 'Alap egységár',
          type: 'number',
          minWidth: 140,
          align: 'right',
          headerAlign: 'right',
          renderCell: (params) => formatNumber(params.row.priceUnitPrice),
        },
        {
          field: 'discountPercent',
          headerName: 'Kedvezmény%',
          type: 'number',
          minWidth: 130,
          align: 'right',
          headerAlign: 'right',
          renderCell: (params) => formatNumber(params.row.discountPercent),
        },
        {
          field: 'unitPrice',
          headerName: 'Egységár',
          type: 'number',
          minWidth: 130,
          align: 'right',
          headerAlign: 'right',
          renderCell: (params) => formatNumber(params.row.unitPrice),
        },
        {
          field: 'priceCurrencyCode',
          headerName: 'Pénznem',
          type: 'string',
          minWidth: 100,
        },
      ]),
    []
  );

  const handleDialogClose = () => {
    setRows([]);
    onClose();
  };

  return (
    <DialogUI open={open} onClose={handleDialogClose} headerContent="Korábbi árak" maxWidth="xl" fullWidth>
      <Box sx={{ mt: 2 }}>
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Cikk
          </Typography>
          <Typography variant="body1">
            {itemCode}
            {itemName ? ` - ${itemName}` : ''}
          </Typography>
        </Box>

        <DatagridPremiumUi
          key={`${open}-${initialPartnerName ?? ''}`}
          datagridIdentifier={DataGridIdentifiers.DocumentPrevPrices}
          datagridInitialState={gridInitialState}
          rows={filteredRows}
          height={600}
          columns={columns}
          headerFilters={false}
          paginationAndfilterMode="client"
          loading={loading}
          filterFromInitialState
          disableSave
          getRowId={(row) => row.id}
        />

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <CircularProgress size={24} />
          </Box>
        )}
      </Box>

      <Box display="flex" justifyContent="center" gap="0.5rem" mt="1rem">
        <ButtonUI text="Bezárás" className="bg-primary" onClick={handleDialogClose} />
      </Box>
    </DialogUI>
  );
};

export default DocumentPrevPricesDialog;
